export type CardType = 'restaurant' | 'lodging' | 'poi' | 'activity' | 'shopping' | 'transport';

export const CARD_TYPE_VALUES: ReadonlyArray<CardType> = [
  'restaurant',
  'lodging',
  'poi',
  'activity',
  'shopping',
  'transport',
] as const;

const SYNONYM_MAP: Record<string, CardType> = {
  // restaurant
  restaurant: 'restaurant',
  meal: 'restaurant',
  food: 'restaurant',
  dining: 'restaurant',
  cafe: 'restaurant',
  bar: 'restaurant',
  eatery: 'restaurant',
  bistro: 'restaurant',
  kitchen: 'restaurant',

  // lodging
  lodging: 'lodging',
  hotel: 'lodging',
  stay: 'lodging',
  accommodation: 'lodging',
  accom: 'lodging',
  inn: 'lodging',
  hostel: 'lodging',
  bnb: 'lodging',

  // poi
  poi: 'poi',
  attraction: 'poi',
  attractions: 'poi',
  sight: 'poi',
  landmark: 'poi',
  spot: 'poi',
  scenic: 'poi',
  place: 'poi',

  // activity
  activity: 'activity',
  experience: 'activity',
  tour: 'activity',
  event: 'activity',
  celebration: 'activity',

  // shopping
  shopping: 'shopping',
  mall: 'shopping',
  store: 'shopping',
  market: 'shopping',

  // transport
  transport: 'transport',
  commute: 'transport',
  bus: 'transport',
  train: 'transport',
  flight: 'transport',
  taxi: 'transport',
  metro: 'transport',
};

export interface NormalizeCardTypeOptions {
  // When input cannot be mapped, return this fallback; use null to indicate "no mapping"
  fallback?: CardType | null;
}

/**
 * Normalize various user/UI inputs to a strict CardType.
 * @param input UI/raw type string (e.g. 'meal', 'hotel', 'sight', 'commute')
 * @param options fallback: default 'poi'; pass null to return null on unknown
 */
export function normalizeCardType(
  input: unknown,
  options: NormalizeCardTypeOptions = { fallback: 'poi' }
): CardType | null {
  if (typeof input !== 'string') return options.fallback ?? null;
  const key = input.trim().toLowerCase();
  const mapped = SYNONYM_MAP[key];
  if (mapped) return mapped;
  return options.fallback ?? null;
}

export function isCardType(v: unknown): v is CardType {
  return typeof v === 'string' && (CARD_TYPE_VALUES as readonly string[]).includes(v);
}

/** Return a safe owner segment for uploads when cardId is absent */
export function ownerSegmentForUpload(input: unknown): CardType {
  const normalized = normalizeCardType(input, { fallback: 'poi' });
  return (normalized || 'poi');
}
